import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useAuth } from "../modules/auth/AuthContext";
import { apiBaseUrl } from "../lib/api";
import { NotificationsDropdown } from "./NotificationsDropdown";
import { useThemeMode } from "../hooks/useThemeMode";

function Icon({ name }: { name: "menu" | "home" | "trend" | "check" | "dumbbell" | "apple" | "history" | "settings" | "bell" | "sun" | "moon" | "chevron" | "search" | "logout" | "profile" | "messages" }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  const paths = {
    menu: <><line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" /></>,
    home: <><path d="m3 11 9-8 9 8" /><path d="M5 10v10h14V10" /><path d="M9 20v-6h6v6" /></>,
    trend: <><path d="M3 17 9 11l4 4 8-8" /><path d="M14 7h7v7" /></>,
    check: <><rect x="4" y="4" width="16" height="18" rx="2" /><path d="M9 2v4" /><path d="M15 2v4" /><path d="m8 13 3 3 5-6" /></>,
    dumbbell: <><path d="M6 6v12" /><path d="M18 6v12" /><path d="M3 9v6" /><path d="M21 9v6" /><path d="M6 12h12" /></>,
    apple: <><path d="M12 6c-3 0-5 2-5 6 0 5 3 8 5 8s5-3 5-8c0-4-2-6-5-6Z" /><path d="M12 6c0-2 1-3 3-4" /><path d="M10 5c-1-2-3-2-4-1" /></>,
    history: <><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 3v6h6" /><path d="M12 7v5l3 2" /></>,
    settings: <><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1A2 2 0 1 1 4.2 17l.1-.1A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.6-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7A2 2 0 1 1 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.6V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1A2 2 0 1 1 19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.1a2 2 0 1 1 0 4H21a1.7 1.7 0 0 0-1.6 1Z" /></>,
    bell: <><path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 21h4" /></>,
    sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2" /><path d="M12 20v2" /><path d="m4.9 4.9 1.4 1.4" /><path d="m17.7 17.7 1.4 1.4" /><path d="M2 12h2" /><path d="M20 12h2" /><path d="m4.9 19.1 1.4-1.4" /><path d="m17.7 6.3 1.4-1.4" /></>,
    moon: <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />,
    chevron: <path d="m6 9 6 6 6-6" />,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" /></>,
    logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></>,
    profile: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
    messages: <><path d="M21 12a8 8 0 0 1-12 7l-5 1 1-4A8 8 0 1 1 21 12z" /></>,
  };

  return <svg {...common}>{paths[name]}</svg>;
}

export function ClientShell() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [topbarSearch, setTopbarSearch] = useState("");
  const chevronRef = useRef<HTMLButtonElement>(null);
  const { theme, toggleTheme } = useThemeMode();

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!profileMenuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (chevronRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest(".sh2-profile-menu")) return;
      setProfileMenuOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profileMenuOpen]);

  const fullName = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim();
  const initial = user?.first_name?.[0]?.toUpperCase() ?? "U";
  const profilePhotoSrc = user?.profile_photo_url ? `${apiBaseUrl}${user.profile_photo_url}` : null;
  const currentTab = new URLSearchParams(location.search).get("tab") ?? "dashboard";
  const basePath = clientId ? `/clients/${clientId}/portal` : "/client";
  const isProfilePage = location.pathname === "/client/profile";
  const isSettingsPage = location.pathname === "/client/settings";
  const isMessagesPage = location.pathname === "/client/messages";

  function handleTopbarSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const query = topbarSearch.trim();
    if (!query) return;

    const normalized = query.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const targetTab =
      /\b(rutina|rutinas|assessment|entreno|entrenamiento)\b/.test(normalized)
        ? "assessment"
        : /\b(check|checkin|check-ins|registro)\b/.test(normalized)
          ? "checkin"
          : /\b(historial|historia|semanas)\b/.test(normalized)
            ? "history"
            : /\b(progreso|peso|medidas|tracking)\b/.test(normalized)
              ? "tracking"
              : "dashboard";

    if (/\b(mensaje|mensajes|chat|coach)\b/.test(normalized) && !clientId) {
      navigate(`/client/messages?q=${encodeURIComponent(query)}`);
      return;
    }

    navigate(`${basePath}?tab=${targetTab}&q=${encodeURIComponent(query)}`);
  }

  const navItems = [
    { tab: "dashboard", label: "Dashboard", icon: "home" as const },
    { tab: "tracking", label: "Mi progreso", icon: "trend" as const },
    { tab: "checkin", label: "Check-ins", icon: "check" as const },
    { tab: "assessment", label: "Rutina", icon: "dumbbell" as const },
    { tab: "history", label: "Historial", icon: "history" as const },
  ];

  return (
    <div className={`sh2-layout${mobileNavOpen ? " sh2-layout--drawer-open" : ""}`}>
      <button
        type="button"
        className="sh2-drawer-overlay"
        aria-label="Cerrar menú"
        tabIndex={mobileNavOpen ? 0 : -1}
        onClick={() => setMobileNavOpen(false)}
      />
      <aside className="sh2-sidebar">
        <Link to={`${basePath}?tab=dashboard`} className="sh2-brand">
          <span className="sh2-brand-logo"><img src="/logos/jc-logo.png" alt="Jorge Carrillo" /></span>
        </Link>

        <nav className="sh2-nav" aria-label="Navegación principal del cliente">
          {navItems.map((item) => (
            <Link
              key={item.tab}
              to={`${basePath}?tab=${item.tab}`}
              className={`sh2-nav-item sh2-nav-item--${item.tab}${!isProfilePage && !isSettingsPage && !isMessagesPage && currentTab === item.tab ? " sh2-nav-item--active" : ""}`}
            >
              <span className="sh2-nav-icon"><Icon name={item.icon} /></span>
              <span className="sh2-nav-label">{item.label}</span>
            </Link>
          ))}
          {!clientId ? (
            <Link to="/client/messages" className={`sh2-nav-item${isMessagesPage ? " sh2-nav-item--active" : ""}`}>
              <span className="sh2-nav-icon"><Icon name="messages" /></span>
              <span className="sh2-nav-label">Mensajes</span>
            </Link>
          ) : null}
          {!clientId ? (
            <Link to="/client/settings" className={`sh2-nav-item${isSettingsPage ? " sh2-nav-item--active" : ""}`}>
              <span className="sh2-nav-icon"><Icon name="settings" /></span>
              <span className="sh2-nav-label">Configuración</span>
            </Link>
          ) : null}
        </nav>

        <section className="sh2-promo" aria-label="Motivación">
          <div className="sh2-promo-body">
            <p className="sh2-promo-headline">Tu cambio empieza hoy.</p>
            <p className="sh2-promo-sub">Constancia. Disciplina. Resultados.</p>
          </div>
          <button type="button" className="sh2-promo-btn" aria-label="Ver rutina">
            <Icon name="dumbbell" />
          </button>
          <img className="sh2-promo-figure" src="/gym-athlete-promo.jpeg" alt="" />
        </section>
      </aside>

      <div className="sh2-body">
        <header className="sh2-topbar">
          <div className="sh2-topbar-left">
            <button
              type="button"
              className="sh2-icon-btn"
              aria-label="Abrir o cerrar menú"
              onClick={() => setMobileNavOpen((value) => !value)}
            >
              <Icon name="menu" />
            </button>
            <form className="sh2-search-wrap" onSubmit={handleTopbarSearch}>
              <span className="sh2-search-ico"><Icon name="search" /></span>
              <input
                className="sh2-search-input"
                type="search"
                placeholder="Buscar rutina, check-ins, historial..."
                value={topbarSearch}
                onChange={(event) => setTopbarSearch(event.target.value)}
              />
            </form>
          </div>

          <div className="sh2-topbar-right">
            <NotificationsDropdown trigger={<Icon name="bell" />} />
            <button
              type="button"
              className="sh2-icon-btn"
              aria-label={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
              title={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
              onClick={toggleTheme}
            >
              <Icon name={theme === "dark" ? "sun" : "moon"} />
            </button>
            <div className="sh2-user-btn">
              <span className="sh2-topbar-avatar sh2-topbar-avatar--photo">
                {profilePhotoSrc ? (
                  <img src={profilePhotoSrc} alt={fullName || "Avatar"} />
                ) : (
                  initial
                )}
              </span>
              <div className="sh2-user-meta">
                <strong>{fullName || "Cuenta cliente"}</strong>
                <span>Cliente</span>
              </div>
              <button
                ref={chevronRef}
                type="button"
                className="sh2-user-chevron"
                aria-label="Abrir menu de usuario"
                aria-expanded={profileMenuOpen}
                onClick={() => setProfileMenuOpen((value) => !value)}
              >
                <Icon name="chevron" />
              </button>
              {profileMenuOpen ? (
                <div className="sh2-profile-menu">
                  <Link className="sh2-profile-item" to={clientId ? `${basePath}?tab=assessment` : "/client/profile"} onClick={() => setProfileMenuOpen(false)}>
                    <Icon name="profile" /> Mi perfil
                  </Link>
                  {!clientId ? (
                    <Link className="sh2-profile-item" to="/client/settings" onClick={() => setProfileMenuOpen(false)}>
                      <Icon name="settings" /> Configuración
                    </Link>
                  ) : null}
                  <button
                    type="button"
                    className="sh2-profile-item"
                    onClick={() => {
                      signOut();
                      setProfileMenuOpen(false);
                    }}
                  >
                    <Icon name="logout" /> Cerrar sesión
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <main className="sh2-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
